// @contexts/StatementClassifierClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";

import type { OptionTs } from "@ps-bridge/interop";
import { requireContext } from "@contexts/requireContext";
import { DefaultStatementClassifierClient } from "@services/clients/DefaultStatementClassifierClient";
import type { StatementClassifierClient } from "@services/clients/StatementClassifierClient";
import { StubStatementClassifierClient } from "@services/stubs/StubStatementClassifierClient";
import * as Option from "@utils/Option";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";

const StatementClassifierClientContext = createContext<OptionTs<StatementClassifierClient>>(Option.none());

type StatementClassifierClientProviderProps = {
    readonly children: ReactNode;
    readonly client: OptionTs<StatementClassifierClient>;
};

export const StatementClassifierClientProvider: React.FC<StatementClassifierClientProviderProps> = ({
    children,
    client: injectedClient,
}) => {
    const client = useMemo<StatementClassifierClient>(
        () => Option.fold(
            injectedClient,
            () => enabledFeatures[FeaturesEnum.StubClients]
                ? new StubStatementClassifierClient(Option.none())
                : new DefaultStatementClassifierClient(),
            (value) => value,
        ),
        [injectedClient],
    );
    const contextValue = useMemo(() => Option.some(client), [client]);

    return (
        <StatementClassifierClientContext.Provider value={contextValue}>
            {children}
        </StatementClassifierClientContext.Provider>
    );
};

export const useStatementClassifierClient = (): StatementClassifierClient =>
    requireContext(
        useContext(StatementClassifierClientContext),
        "useStatementClassifierClient must be used within a StatementClassifierClientProvider",
    );
